import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { IWidgetContentProps } from '../../../../Dashboard/Components/Widget/Widget';
import { ActiveDatabaseContext } from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import query from './Queries/StatistikAktiviteterQuery.gql';
import { EDTypography } from '../../../../../BaseComponents/Typography/EDTypography';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import {
  StatistikrapporteringWidget as StatistikrapporteringWidgetQuery,
  StatistikrapporteringWidgetVariables,
} from './Queries/_QueryTypes/StatistikrapporteringWidget';
import EDIcon from '../../../../../BaseComponents/Icon/EDIcon';
import {
  TEDBOAktivitetStatistikStatusEnum,
  TEDSelectionConditionType,
  TEDSelectionOperator,
} from '../../../../../globalQueryTypes';
import {
  buildBasePath,
  useBaseRouteValue,
} from '../../../../Routing/Utils/routerUtils';
import { useNavigate } from 'react-router';
import { formatDayLabel, getDateString } from './Utils/Utils';
import { StatistikSkeletonLoader } from './Components/StatistikSkeletonLoader';
import { NoAccessBanner } from './Components/NoAccessBanner';
import { ReportingStatus } from './Components/ReportingStatus';
import { LoginStateContext } from '../../../../Login/Contexts/LoginStateContext/LoginStateContext';
import { useActiveDBCollectionQuery } from '../../../../../Components/QueryComponents/ActiveDBCollectionQuery/ActiveDBCollectionQuery';
import { rutinInfos } from '../../../_Common/Const/RutinInfo';
import { rutiner } from '../../../_Common/Const/Rutiner';
import {
  BOBehorighetsKontroll,
  BOBehorighetsKontrollVariables,
} from '../../Rutiner/Statistikrapportering/Queries/_QueryTypes/BOBehorighetsKontroll';
import BOBehorighetsKontrollQuery from '../../Rutiner/Statistikrapportering/Queries/BOBehorighetsKontroll.gql';
import { styled } from '@mui/system';
import { Theme, useTheme } from '@mui/material/styles';
import { themeColors } from '../../../../../BaseComponents/ThemeProvider/AvenyThemeColors';

const TODAY_STATUSES = new Set<TEDBOAktivitetStatistikStatusEnum>([
  TEDBOAktivitetStatistikStatusEnum.STATISTIK_RAPPORTERAD,
  TEDBOAktivitetStatistikStatusEnum.STATISTIK_EXPORTERAD,
  TEDBOAktivitetStatistikStatusEnum.SKA_RAPPORTERAS,
  TEDBOAktivitetStatistikStatusEnum.DET_AR_MINDRE_AN_7_DAGAR_KVAR_ATT_RAPPORTERA_STATISTIK_PA_AKTIVITETEN,
]);

const OTHER_DAY_STATUSES = new Set<TEDBOAktivitetStatistikStatusEnum>([
  TEDBOAktivitetStatistikStatusEnum.SKA_RAPPORTERAS,
  TEDBOAktivitetStatistikStatusEnum.DET_AR_MINDRE_AN_7_DAGAR_KVAR_ATT_RAPPORTERA_STATISTIK_PA_AKTIVITETEN,
]);

// Logic for how many days and activities to show in the widget, starting from today and going backwards:
const MAX_DAYS_BACK = 30;
const INITIAL_TOTAL_ROWS = 8;

export const StatistikrapporteringWidget: React.FC<
  IWidgetContentProps
> = () => {
  const baseRouteValues = useBaseRouteValue();
  const navigate = useNavigate();
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const loginStateContext = useContext(LoginStateContext);

  const { data: boBehorighetsData, loading: loadingBoBehorighetsData } =
    useQuery<BOBehorighetsKontroll, BOBehorighetsKontrollVariables>(
      BOBehorighetsKontrollQuery,
      {
        variables: { dbname: activeDatabase ?? '' },
        skip: !activeDatabase || !loginStateContext?.isLoggedIn,
      }
    );

  const theme = useTheme<Theme>();

  const hasStatisticsAccess =
    boBehorighetsData?.rtn?.BOBehorighetsKontroll?.statusOK ?? false;

  const todayStr = getDateString(0);
  const yesterdayStr = getDateString(1);
  const fromDateStr = getDateString(MAX_DAYS_BACK);
  const statistikrapporteringRutinInfo = rutinInfos.rtnStatistikrapporteringNew;
  const statistikrapporteringInitialUrvalId =
    rutiner[statistikrapporteringRutinInfo.rutinKod]?.initialUrval;

  const { data, loading, error } = useQuery<
    StatistikrapporteringWidgetQuery,
    StatistikrapporteringWidgetVariables
  >(query, {
    skip: !activeDatabase,
    variables: {
      dbname: activeDatabase || '',
      routine: 1038,
      userSelection: [
        [
          {
            namn: 'FRANDATUM',
            typ: TEDSelectionConditionType.Value,
            valueCondition: {
              op: TEDSelectionOperator.interval,
              value: [{ value: fromDateStr, tovalue: todayStr }],
            },
          },
          {
            namn: 'STATISTIKUPPGIFT',
            typ: TEDSelectionConditionType.Boolean,
            booleanCondition: {
              yes: true,
            },
          },
          {
            namn: 'PRELBOKAD',
            typ: TEDSelectionConditionType.Boolean,
            booleanCondition: {
              no: true,
            },
          },
        ],
      ],
    },
  });

  const {
    data: statistikrapporteringScopeRows,
    loading: loadingStatistikrapporteringScopeRows,
    serverError: statistikrapporteringScopeError,
  } = useActiveDBCollectionQuery({
    collectionName: 'BOAktivitet',
    queryName: `${statistikrapporteringRutinInfo.rutinKod}Main`,
    attributeDefs: ['StatistikStatus'],
    urvalId: statistikrapporteringInitialUrvalId,
    routineID: statistikrapporteringRutinInfo.rutinID,
    networkOnlyOnFirst: true,
    skip: !activeDatabase || !hasStatisticsAccess || loading || !!error,
  });

  const dayGroups = React.useMemo(() => {
    const all = data?.db?.BOAktivitet_All ?? [];
    const result: { date: string; activities: typeof all }[] = [];

    let totalRows = INITIAL_TOTAL_ROWS;
    let totalShown = 0;
    let daysAdded = 0;

    for (let offset = 0; offset <= MAX_DAYS_BACK && daysAdded < 4; offset++) {
      const dateStr = getDateString(offset);
      const allowedStatuses =
        dateStr === todayStr ? TODAY_STATUSES : OTHER_DAY_STATUSES;
      const activities = all.filter(
        a =>
          a?.FRANDATUM === dateStr &&
          a?.StatistikStatus != null &&
          allowedStatuses.has(a.StatistikStatus)
      );

      if (activities.length === 0) continue;

      if (daysAdded === 0) {
        // Day 1: always cap at totalRows (8)
        const sliced = activities.slice(0, totalRows);
        result.push({ date: dateStr, activities: sliced });
        totalShown += sliced.length;
        daysAdded++;

        if (activities.length <= 6) {
          totalRows = 7;
        } else {
          // 7 or 8+ items on day 1: no more days
          break;
        }
      } else if (daysAdded === 1) {
        // Day 2: fill remaining slots up to totalRows (7)
        const remaining = totalRows - totalShown;
        if (remaining <= 0) break;

        const sliced = activities.slice(0, remaining);
        result.push({ date: dateStr, activities: sliced });
        totalShown += sliced.length;
        daysAdded++;

        if (totalShown >= 6) break; // No room for day 3
      } else if (daysAdded === 2) {
        // Day 3: grand total must stay <= 6 (max 6), and Day 3 itself max 3 items
        const remaining = Math.min(3, 6 - totalShown);
        if (remaining <= 0) break;

        const sliced = activities.slice(0, remaining);
        result.push({ date: dateStr, activities: sliced });
        totalShown += sliced.length;
        daysAdded++;
      } else if (daysAdded === 3) {
        // Day 4: only shown if the previous 3 days had exactly 1 item each
        if (totalShown !== 3) break;

        result.push({ date: dateStr, activities: activities.slice(0, 1) });
        daysAdded++;
      }
    }

    return result;
  }, [data?.db?.BOAktivitet_All, todayStr]);

  const remainingAktiviteterToReport = React.useMemo(() => {
    const all = statistikrapporteringScopeRows ?? [];
    return all.filter(
      a =>
        a?.StatistikStatus ===
          TEDBOAktivitetStatistikStatusEnum.SKA_RAPPORTERAS ||
        a?.StatistikStatus ===
          TEDBOAktivitetStatistikStatusEnum.DET_AR_MINDRE_AN_7_DAGAR_KVAR_ATT_RAPPORTERA_STATISTIK_PA_AKTIVITETEN
    ).length;
  }, [statistikrapporteringScopeRows]);

  const navigateToStatistikrapportering = (
    aktivitetId?: string | null,
    isReported?: boolean
  ) => {
    const basePath =
      buildBasePath(baseRouteValues) +
      `${isReported ? '/Rutin/Kalender' : '/Rutin/Statistikrapportering'}`;
    navigate(aktivitetId ? `${basePath}/${aktivitetId}` : basePath);
  };

  const navigateToStatistikrapporteringBasePath = () => {
    const basePath =
      buildBasePath(baseRouteValues) + '/Rutin/Statistikrapportering';
    navigate(basePath);
  };

  return loading ||
    loadingBoBehorighetsData ||
    loadingStatistikrapporteringScopeRows ? (
    <StatistikSkeletonLoader count={5} />
  ) : error ? (
    <EDTypography>{error.message}</EDTypography>
  ) : statistikrapporteringScopeError ? (
    <EDTypography>{statistikrapporteringScopeError.message}</EDTypography>
  ) : (
    <EDBox
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ml: 1,
      }}
    >
      {dayGroups.map(({ date, activities }) => (
        <EDBox key={date}>
          <EDTypography variant="body2" bold sx={{ mb: 0.5 }}>
            {formatDayLabel(date, todayStr, yesterdayStr)}
          </EDTypography>

          {activities.map(aktivitet => {
            const isReported =
              aktivitet?.StatistikStatus ===
                TEDBOAktivitetStatistikStatusEnum.STATISTIK_RAPPORTERAD ||
              aktivitet?.StatistikStatus ===
                TEDBOAktivitetStatistikStatusEnum.STATISTIK_EXPORTERAD;

            return (
              <RowContainer
                key={aktivitet?.id}
                onClick={() =>
                  navigateToStatistikrapportering(aktivitet?.id, isReported)
                }
              >
                <EDTypography
                  fontSize={'12px'}
                  sx={{
                    color: isReported
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary,
                  }}
                >
                  {aktivitet?.FRANTID?.slice(0, 5)}
                </EDTypography>
                <EDTypography
                  fontSize={'12px'}
                  sx={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: isReported
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary,
                  }}
                >
                  {aktivitet?.TITEL ?? ''}
                </EDTypography>
                <EDBox sx={{ visibility: isReported ? 'visible' : 'hidden' }}>
                  <EDIcon
                    size="medium"
                    iconType={'check'}
                    customSvgColor={theme.palette.primary.light}
                  />
                </EDBox>
              </RowContainer>
            );
          })}
        </EDBox>
      ))}

      {hasStatisticsAccess ? (
        <ReportingStatus
          remainingCount={remainingAktiviteterToReport}
          onClick={navigateToStatistikrapporteringBasePath}
        />
      ) : (
        <NoAccessBanner />
      )}
    </EDBox>
  );
};

const RowContainer = styled(EDBox)(({ theme: muiTheme }) => {
  const theme = muiTheme as Theme;
  return {
    display: 'flex',
    gap: 4,
    marginBottom: 8,
    alignItems: 'baseline',
    maxWidth: '285px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: themeColors.blueGrayFade[200.87],
    },
    border: `0.5px solid ${theme.customProperties.Fade.DarkOnLight.divider}`,
    backgroundColor: themeColors.blueGray[50],
    padding: '2px 8px',
    borderRadius: 6,
  };
});
